import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, Plus, Camera, ChevronLeft, ChevronRight, Heart, Send, 
  Eye, MoreHorizontal, Trash2, Image, Video, Type, Sparkles,
  Pause, Play
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Story Ring Component - Shows a user's story bubble
export const StoryRing = ({ user, hasStories, hasUnseen, onClick, size = 'md' }) => {
  const sizes = {
    sm: 'w-14 h-14',
    md: 'w-16 h-16',
    lg: 'w-20 h-20'
  };
  
  const innerSizes = {
    sm: 'w-12 h-12',
    md: 'w-14 h-14',
    lg: 'w-[72px] h-[72px]'
  };

  return (
    <button
      onClick={onClick}
      className="flex flex-col items-center gap-1"
      data-testid={`story-ring-${user.id}`}
    >
      <div className={`${sizes[size]} rounded-full p-[2px] ${
        hasUnseen 
          ? 'bg-gradient-to-tr from-[#FF2A2A] via-[#FFE800] to-[#00F0FF]' 
          : hasStories 
            ? 'bg-gray-600' 
            : 'bg-transparent'
      }`}>
        <div className={`${innerSizes[size]} rounded-full bg-[#030305] p-[2px]`}>
          <img
            src={user.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name)}&background=0F0F16&color=00F0FF`}
            alt={user.name}
            className="w-full h-full rounded-full object-cover"
          />
        </div>
      </div>
      <span className="text-xs text-gray-400 truncate max-w-[64px]">
        {user.name?.split(' ')[0]}
      </span>
    </button>
  );
};

// Stories Bar Component - Horizontal scrollable list of story rings
export const StoriesBar = ({ onStoryClick, onCreateStory }) => {
  const { user } = useAuth();
  const [storiesFeed, setStoriesFeed] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStoriesFeed();
    const interval = setInterval(fetchStoriesFeed, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, []);

  const fetchStoriesFeed = async () => {
    try {
      const response = await axios.get(`${API}/stories/feed`);
      setStoriesFeed(response.data);
    } catch (error) {
      console.error('Error fetching stories:', error);
    } finally {
      setLoading(false);
    }
  };

  const myStories = storiesFeed.find(s => s.user_id === user?.id);
  const otherStories = storiesFeed.filter(s => s.user_id !== user?.id);

  return (
    <div className="flex items-center gap-4 px-4 py-3 overflow-x-auto scrollbar-hide bg-[#08080C] border-b border-white/5">
      {/* Add Story Button */}
      <div className="flex flex-col items-center gap-1 flex-shrink-0">
        <button
          onClick={onCreateStory}
          className="w-16 h-16 rounded-full bg-[#0F0F16] border-2 border-dashed border-[#00F0FF]/30 flex items-center justify-center hover:bg-[#00F0FF]/10 hover:border-[#00F0FF] transition-all"
          data-testid="create-story-button"
        >
          <Plus className="w-6 h-6 text-[#00F0FF]" />
        </button>
        <span className="text-xs text-gray-400">Add Story</span>
      </div>

      {/* My Stories */}
      {myStories && (
        <StoryRing
          user={{ id: user.id, name: 'Your Story', avatar: user.avatar }}
          hasStories={true}
          hasUnseen={false}
          onClick={() => onStoryClick(myStories)}
        />
      )}

      {/* Other Users' Stories */}
      {otherStories.map((userStory) => (
        <StoryRing
          key={userStory.user_id}
          user={{ id: userStory.user_id, name: userStory.user_name, avatar: userStory.user_avatar }}
          hasStories={true}
          hasUnseen={userStory.has_unseen}
          onClick={() => onStoryClick(userStory)}
        />
      ))}

      {loading && (
        <div className="flex gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="w-16 h-16 rounded-full bg-[#0F0F16] animate-pulse" />
          ))}
        </div>
      )}
    </div>
  );
};

// Story Viewer Component - Full-screen story viewing experience
export const StoryViewer = ({ userStory, onClose, onNext, onPrev, hasNext, hasPrev }) => {
  const { user } = useAuth();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showReactions, setShowReactions] = useState(false);
  const [replyText, setReplyText] = useState('');
  const progressInterval = useRef(null);

  const stories = userStory?.stories || [];
  const currentStory = stories[currentIndex];

  useEffect(() => {
    if (!currentStory || isPaused) return;
    
    // Mark as viewed
    markAsViewed(currentStory.id);
    
    // Progress timer
    const duration = (currentStory.duration || 5) * 1000;
    const step = 100 / (duration / 50);
    
    setProgress(0);
    progressInterval.current = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(progressInterval.current);
          goToNext();
          return 0;
        }
        return prev + step;
      });
    }, 50);

    return () => clearInterval(progressInterval.current);
  }, [currentIndex, currentStory, isPaused]);

  const markAsViewed = async (storyId) => {
    try {
      await axios.post(`${API}/stories/${storyId}/view`);
    } catch (error) {
      console.error('Error marking story as viewed:', error);
    }
  };

  const goToNext = () => {
    if (currentIndex < stories.length - 1) {
      setCurrentIndex(prev => prev + 1);
    } else if (hasNext) {
      onNext();
    } else {
      onClose();
    }
  };

  const goToPrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    } else if (hasPrev) {
      onPrev();
    }
  };

  const handleReaction = async (emoji) => {
    try {
      await axios.post(`${API}/stories/${currentStory.id}/react`, {
        emoji,
        story_id: currentStory.id
      });
      setShowReactions(false);
    } catch (error) {
      console.error('Error reacting to story:', error);
    }
  };

  const handleReply = async () => {
    if (!replyText.trim()) return;
    // This would send a DM to the story owner
    console.log('Reply:', replyText);
    setReplyText('');
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this story?')) return;
    try {
      await axios.delete(`${API}/stories/${currentStory.id}`);
      if (stories.length === 1) {
        onClose();
      } else {
        goToNext();
      }
    } catch (error) {
      console.error('Error deleting story:', error);
    }
  };

  if (!currentStory) return null;

  const isOwner = currentStory.user_id === user?.id;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black z-50 flex items-center justify-center"
      data-testid="story-viewer"
    >
      {/* Story Container */}
      <div className="relative w-full max-w-[420px] h-full max-h-[90vh] bg-[#030305] rounded-2xl overflow-hidden mx-4">
        {/* Progress bars */}
        <div className="absolute top-2 left-2 right-2 z-20 flex gap-1">
          {stories.map((_, idx) => (
            <div key={idx} className="flex-1 h-1 bg-white/20 rounded-full overflow-hidden">
              <div 
                className="h-full bg-white rounded-full transition-all"
                style={{ 
                  width: idx < currentIndex ? '100%' : idx === currentIndex ? `${progress}%` : '0%'
                }}
              />
            </div>
          ))}
        </div>

        {/* Header */}
        <div className="absolute top-6 left-0 right-0 z-20 flex items-center justify-between px-4">
          <div className="flex items-center gap-3">
            <img
              src={userStory.user_avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(userStory.user_name)}&background=0F0F16&color=00F0FF`}
              alt={userStory.user_name}
              className="w-10 h-10 rounded-full border-2 border-white/20"
            />
            <div>
              <p className="text-white font-semibold text-sm">{userStory.user_name}</p>
              <p className="text-white/60 text-xs">
                {new Date(currentStory.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsPaused(!isPaused)}
              className="p-2 rounded-full hover:bg-white/10"
            >
              {isPaused ? <Play className="w-5 h-5 text-white" /> : <Pause className="w-5 h-5 text-white" />}
            </button>
            {isOwner && (
              <button
                onClick={handleDelete}
                className="p-2 rounded-full hover:bg-white/10"
                data-testid="delete-story-button"
              >
                <Trash2 className="w-5 h-5 text-white" />
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-white/10"
              data-testid="close-story-viewer"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>

        {/* Story Content */}
        <div 
          className="absolute inset-0 flex items-center justify-center"
          style={{ backgroundColor: currentStory.background_color || '#030305' }}
          onClick={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            const x = e.clientX - rect.left;
            if (x < rect.width / 3) {
              goToPrev();
            } else if (x > rect.width * 2 / 3) {
              goToNext();
            } else {
              setIsPaused(!isPaused);
            }
          }}
        >
          {currentStory.media_type === 'video' ? (
            <video
              src={currentStory.media_url}
              className="w-full h-full object-contain"
              autoPlay
              muted
              loop
            />
          ) : (
            <img
              src={currentStory.media_url}
              alt="Story"
              className="w-full h-full object-contain"
            />
          )}

          {/* Text Overlay */}
          {currentStory.text_overlay && (
            <div className={`absolute left-4 right-4 text-center ${
              currentStory.text_position === 'top' ? 'top-24' :
              currentStory.text_position === 'bottom' ? 'bottom-24' : 'top-1/2 -translate-y-1/2'
            }`}>
              <p className="text-white text-xl font-bold drop-shadow-lg">
                {currentStory.text_overlay}
              </p>
            </div>
          )}

          {/* Caption */}
          {currentStory.caption && (
            <div className="absolute bottom-20 left-4 right-4">
              <p className="text-white text-sm bg-black/50 rounded-lg px-3 py-2 backdrop-blur-sm">
                {currentStory.caption}
              </p>
            </div>
          )}
        </div>

        {/* Navigation Arrows */}
        {(currentIndex > 0 || hasPrev) && (
          <button
            onClick={goToPrev}
            className="absolute left-2 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-black/30 hover:bg-black/50"
          >
            <ChevronLeft className="w-6 h-6 text-white" />
          </button>
        )}
        {(currentIndex < stories.length - 1 || hasNext) && (
          <button
            onClick={goToNext}
            className="absolute right-2 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-black/30 hover:bg-black/50"
          >
            <ChevronRight className="w-6 h-6 text-white" />
          </button>
        )}

        {/* Footer - Reactions & Reply */}
        {!isOwner && (
          <div className="absolute bottom-4 left-4 right-4 z-20 flex items-center gap-2">
            <input
              type="text"
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder="Send a message..."
              className="flex-1 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white placeholder-white/50 outline-none focus:border-[#00F0FF]"
              data-testid="story-reply-input"
            />
            <button
              onClick={() => setShowReactions(!showReactions)}
              className="p-2 rounded-full bg-white/10 hover:bg-white/20"
            >
              <Heart className="w-5 h-5 text-white" />
            </button>
            <button
              onClick={handleReply}
              className="p-2 rounded-full bg-[#00F0FF] text-black"
              data-testid="send-story-reply"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* View Count (for owner) */}
        {isOwner && currentStory.view_count > 0 && (
          <div className="absolute bottom-4 left-4 z-20 flex items-center gap-2 text-white/80">
            <Eye className="w-4 h-4" />
            <span className="text-sm">{currentStory.view_count} views</span>
          </div>
        )}

        {/* Reactions Popup */}
        <AnimatePresence>
          {showReactions && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              className="absolute bottom-20 right-4 z-30 flex gap-2 bg-[#08080C] rounded-full px-4 py-2 border border-white/10"
            >
              {['❤️', '🔥', '😂', '😮', '😢', '🙏'].map((emoji) => (
                <button
                  key={emoji}
                  onClick={() => handleReaction(emoji)}
                  className="text-2xl hover:scale-125 transition-transform"
                >
                  {emoji}
                </button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

// Create Story Modal
export const CreateStoryModal = ({ onClose, onCreated }) => {
  const [mediaUrl, setMediaUrl] = useState('');
  const [mediaType, setMediaType] = useState('image');
  const [caption, setCaption] = useState('');
  const [textOverlay, setTextOverlay] = useState('');
  const [textPosition, setTextPosition] = useState('center');
  const [backgroundColor, setBackgroundColor] = useState('#030305');
  const [duration, setDuration] = useState(5);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setMediaUrl(response.data.url);
      setMediaType(file.type.startsWith('video/') ? 'video' : 'image');
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleCreate = async () => {
    if (!mediaUrl && !textOverlay) {
      alert('Please add media or text to your story');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/stories`, {
        media_url: mediaUrl || 'https://via.placeholder.com/1080x1920/030305/030305',
        media_type: mediaUrl ? mediaType : 'image',
        caption,
        text_overlay: textOverlay,
        text_position: textPosition,
        background_color: backgroundColor,
        duration
      });
      
      onCreated();
      onClose();
    } catch (error) {
      console.error('Error creating story:', error);
      alert('Failed to create story');
    } finally {
      setLoading(false);
    }
  };

  const backgroundColors = [
    '#030305', '#FF2A2A', '#FFE800', '#00F0FF', '#00FF66', 
    '#A78BFA', '#F472B6', '#1E3A8A', '#065F46'
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-[#08080C] rounded-3xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto border border-white/10"
        data-testid="create-story-modal"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>
            Create Story
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-white/10 text-gray-400"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Story Preview */}
        <div 
          className="relative aspect-[9/16] max-h-[400px] rounded-2xl overflow-hidden mb-6 flex items-center justify-center"
          style={{ backgroundColor }}
        >
          {mediaUrl ? (
            mediaType === 'video' ? (
              <video src={mediaUrl} className="w-full h-full object-contain" autoPlay muted loop />
            ) : (
              <img src={mediaUrl} alt="Preview" className="w-full h-full object-contain" />
            )
          ) : (
            <div className="text-center text-white/50">
              <Sparkles className="w-12 h-12 mx-auto mb-2" />
              <p>Add media or text</p>
            </div>
          )}

          {textOverlay && (
            <div className={`absolute left-4 right-4 text-center ${
              textPosition === 'top' ? 'top-8' :
              textPosition === 'bottom' ? 'bottom-8' : 'top-1/2 -translate-y-1/2'
            }`}>
              <p className="text-white text-xl font-bold drop-shadow-lg">
                {textOverlay}
              </p>
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="space-y-4">
          {/* Upload Media */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Media</label>
            <div className="flex gap-2">
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="flex-1 py-3 rounded-xl bg-[#0F0F16] border border-white/10 text-white flex items-center justify-center gap-2 hover:bg-white/5 disabled:opacity-50"
              >
                {uploading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <Image className="w-5 h-5" />
                    Photo
                  </>
                )}
              </button>
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="flex-1 py-3 rounded-xl bg-[#0F0F16] border border-white/10 text-white flex items-center justify-center gap-2 hover:bg-white/5 disabled:opacity-50"
              >
                <Video className="w-5 h-5" />
                Video
              </button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/*"
              onChange={handleFileUpload}
              className="hidden"
            />
          </div>

          {/* Text Overlay */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              <Type className="w-4 h-4 inline mr-2" />
              Text Overlay
            </label>
            <input
              type="text"
              value={textOverlay}
              onChange={(e) => setTextOverlay(e.target.value)}
              placeholder="Add text to your story..."
              className="w-full px-4 py-3 rounded-xl bg-[#0F0F16] border border-white/10 text-white placeholder-gray-500 outline-none focus:border-[#00F0FF]"
              data-testid="story-text-input"
            />
            {textOverlay && (
              <div className="flex gap-2 mt-2">
                {['top', 'center', 'bottom'].map((pos) => (
                  <button
                    key={pos}
                    onClick={() => setTextPosition(pos)}
                    className={`px-3 py-1 rounded-lg text-sm capitalize ${
                      textPosition === pos 
                        ? 'bg-[#00F0FF] text-black' 
                        : 'bg-white/5 text-gray-400'
                    }`}
                  >
                    {pos}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Background Color */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Background Color</label>
            <div className="flex gap-2 flex-wrap">
              {backgroundColors.map((color) => (
                <button
                  key={color}
                  onClick={() => setBackgroundColor(color)}
                  className={`w-8 h-8 rounded-full border-2 ${
                    backgroundColor === color ? 'border-white' : 'border-transparent'
                  }`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>

          {/* Caption */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Caption</label>
            <textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Add a caption..."
              rows={2}
              className="w-full px-4 py-3 rounded-xl bg-[#0F0F16] border border-white/10 text-white placeholder-gray-500 outline-none focus:border-[#00F0FF] resize-none"
              data-testid="story-caption-input"
            />
          </div>

          {/* Duration */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Duration: {duration}s
            </label>
            <input
              type="range"
              min={3}
              max={15}
              value={duration}
              onChange={(e) => setDuration(parseInt(e.target.value))}
              className="w-full accent-[#00F0FF]"
            />
          </div>

          {/* Create Button */}
          <motion.button
            onClick={handleCreate}
            disabled={loading || (!mediaUrl && !textOverlay)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="w-full py-4 rounded-xl font-bold text-black bg-gradient-to-r from-[#FF2A2A] via-[#FFE800] to-[#00F0FF] disabled:opacity-50 shadow-lg"
            data-testid="publish-story-button"
          >
            {loading ? 'Publishing...' : 'Share Story'}
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Main Stories Component
const Stories = () => {
  const [viewingStory, setViewingStory] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [storiesFeed, setStoriesFeed] = useState([]);
  const [currentStoryIndex, setCurrentStoryIndex] = useState(0);

  const fetchStoriesFeed = async () => {
    try {
      const response = await axios.get(`${API}/stories/feed`);
      setStoriesFeed(response.data);
    } catch (error) {
      console.error('Error fetching stories:', error);
    }
  };

  const handleStoryClick = (userStory) => {
    const index = storiesFeed.findIndex(s => s.user_id === userStory.user_id);
    setCurrentStoryIndex(index);
    setViewingStory(userStory);
  };

  const handleNextUser = () => {
    if (currentStoryIndex < storiesFeed.length - 1) {
      setCurrentStoryIndex(prev => prev + 1);
      setViewingStory(storiesFeed[currentStoryIndex + 1]);
    } else {
      setViewingStory(null);
    }
  };

  const handlePrevUser = () => {
    if (currentStoryIndex > 0) {
      setCurrentStoryIndex(prev => prev - 1);
      setViewingStory(storiesFeed[currentStoryIndex - 1]);
    }
  };

  return (
    <>
      <StoriesBar 
        onStoryClick={handleStoryClick}
        onCreateStory={() => setShowCreateModal(true)}
      />

      <AnimatePresence>
        {viewingStory && (
          <StoryViewer
            userStory={viewingStory}
            onClose={() => setViewingStory(null)}
            onNext={handleNextUser}
            onPrev={handlePrevUser}
            hasNext={currentStoryIndex < storiesFeed.length - 1}
            hasPrev={currentStoryIndex > 0}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showCreateModal && (
          <CreateStoryModal
            onClose={() => setShowCreateModal(false)}
            onCreated={fetchStoriesFeed}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default Stories;
